// ============================================================
// CineSync — YouTube Utility & IFrame API Loader
// ============================================================

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/**
 * Extracts and validates an 11-character YouTube video ID from any supported format:
 * - Direct 11-char ID (e.g. "dQw4w9WgXcQ")
 * - Standard watch URL (e.g. "https://www.youtube.com/watch?v=dQw4w9WgXcQ")
 * - Shortened share URL (e.g. "https://youtu.be/dQw4w9WgXcQ?si=...")
 * - Shorts (e.g. "https://www.youtube.com/shorts/dQw4w9WgXcQ")
 * - Embed URL (e.g. "https://www.youtube.com/embed/dQw4w9WgXcQ")
 * - Full iframe embed snippet (`<iframe src="..."></iframe>`)
 * - Live URL (e.g. "https://www.youtube.com/live/dQw4w9WgXcQ")
 * - Music / Gaming subdomains
 * - URLs missing protocol prefix ("youtube.com/watch?v=...")
 * - URLs wrapped in quotes, brackets, or markdown
 */
export function extractYouTubeVideoId(input: string): string | null {
  if (!input || typeof input !== 'string') return null;

  let clean = input.trim();

  // If input contains an iframe tag, extract the src URL
  const iframeMatch = clean.match(/<iframe[^>]*\s+src=["']([^"']+)["']/i);
  if (iframeMatch && iframeMatch[1]) {
    clean = iframeMatch[1].trim();
  }

  // Strip wrapping quotes, brackets, angle brackets, or markdown
  clean = clean.replace(/^[<"'`([\]+|[>"'`\])]+$/g, '').trim();

  // 1. Direct 11-character ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(clean)) {
    return clean;
  }

  // 2. Structured URL parsing
  try {
    let urlStr = clean;
    if (!/^https?:\/\//i.test(urlStr)) {
      urlStr = 'https://' + urlStr;
    }
    const parsed = new URL(urlStr);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');

    // youtu.be/<id>
    if (hostname === 'youtu.be') {
      const id = parsed.pathname.replace(/^\/+/, '').split('/')[0];
      if (/^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
    }

    // youtube.com, m.youtube.com, music.youtube.com, youtube-nocookie.com, youtube.co.uk, etc.
    if (hostname.includes('youtube.') || hostname.includes('youtube-nocookie.')) {
      // 1. Standard watch?v= parameter
      const v = parsed.searchParams.get('v');
      if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;

      // 2. /shorts/<id>
      const shortsMatch = parsed.pathname.match(/\/shorts\/([a-zA-Z0-9_-]{11})/i);
      if (shortsMatch) return shortsMatch[1];

      // 3. /embed/<id> or /e/<id>
      const embedMatch = parsed.pathname.match(/\/(?:embed|e)\/([a-zA-Z0-9_-]{11})/i);
      if (embedMatch) return embedMatch[1];

      // 4. /live/<id>
      const liveMatch = parsed.pathname.match(/\/live\/([a-zA-Z0-9_-]{11})/i);
      if (liveMatch) return liveMatch[1];

      // 5. /v/<id> or /watch/<id>
      const vPathMatch = parsed.pathname.match(/\/(?:v|watch)\/([a-zA-Z0-9_-]{11})/i);
      if (vPathMatch) return vPathMatch[1];
    }
  } catch {
    // Continue to regex fallback
  }

  // 3. Robust Regex Fallback (catches surrounding text or non-standard formats)
  const regexPatterns = [
    /(?:v=|v\/|embed\/|e\/|shorts\/|live\/|youtu\.be\/|\/v\/|\/watch\/)([a-zA-Z0-9_-]{11})/i,
    /(?:youtube\.[a-z.]+\/watch\?.*v=)([a-zA-Z0-9_-]{11})/i,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/i,
    /(?:^|\s)([a-zA-Z0-9_-]{11})(?:\s|$)/,
  ];

  for (const pattern of regexPatterns) {
    const match = clean.match(pattern);
    if (match && match[1] && /^[a-zA-Z0-9_-]{11}$/.test(match[1])) {
      return match[1];
    }
  }

  return null;
}

export interface YouTubeMeta {
  title: string;
  authorName: string;
  authorUrl?: string;
  thumbnailUrl?: string;
}

/**
 * Fetches public YouTube video metadata with high reliability.
 * 1. Tries Google's official public YouTube oEmbed endpoint (zero API keys, CORS enabled, fast).
 * 2. Falls back to noembed.com if the official endpoint times out or is blocked.
 * 3. Falls back immediately to reliable high-res thumbnail and standard title.
 */
export async function fetchYouTubeMeta(videoId: string): Promise<YouTubeMeta | null> {
  const fallbackThumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  const defaultTitle = `YouTube Video (${videoId})`;
  const defaultAuthor = 'YouTube Creator';
  const targetUrl = `https://www.youtube.com/watch?v=${videoId}`;

  // 1. Try official YouTube oEmbed endpoint
  try {
    const officialEndpoint = `https://www.youtube.com/oembed?url=${encodeURIComponent(targetUrl)}&format=json`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);

    const res = await fetch(officialEndpoint, {
      method: 'GET',
      signal: controller.signal,
    });
    clearTimeout(timer);

    if (res.ok) {
      const data = await res.json();
      return {
        title: data.title || defaultTitle,
        authorName: data.author_name || defaultAuthor,
        authorUrl: data.author_url,
        thumbnailUrl: data.thumbnail_url || fallbackThumb,
      };
    }
  } catch {
    // Attempt secondary fallback below
  }

  // 2. Try noembed fallback
  try {
    const fallbackEndpoint = `https://noembed.com/embed?url=${encodeURIComponent(targetUrl)}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2500);

    const res = await fetch(fallbackEndpoint, {
      method: 'GET',
      signal: controller.signal,
    });
    clearTimeout(timer);

    if (res.ok) {
      const data = await res.json();
      return {
        title: data.title || defaultTitle,
        authorName: data.author_name || defaultAuthor,
        authorUrl: data.author_url,
        thumbnailUrl: data.thumbnail_url || fallbackThumb,
      };
    }
  } catch {
    // Ultimate fallback below
  }

  // 3. Guaranteed safe fallback
  return {
    title: defaultTitle,
    authorName: defaultAuthor,
    thumbnailUrl: fallbackThumb,
  };
}

let ytApiPromise: Promise<any> | null = null;

/**
 * Loads the official YouTube IFrame Player API script asynchronously.
 * Returns a promise that resolves when window.YT.Player is ready to instantiate.
 */
export function loadYouTubeIFrameAPI(): Promise<any> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window not available'));
  }

  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }

  if (ytApiPromise) {
    return ytApiPromise;
  }

  ytApiPromise = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('YouTube IFrame API script load timed out'));
    }, 12000);

    const prevReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      clearTimeout(timeout);
      if (typeof prevReady === 'function') prevReady();
      resolve(window.YT);
    };

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
    if (!existingScript) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = true;
      tag.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load YouTube IFrame API script'));
      };
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        document.head.appendChild(tag);
      }
    }
  });

  return ytApiPromise;
}
